import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Package, ShoppingCart, Users, Mail, DollarSign, TrendingUp, ChevronRight, Settings, Layers, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    if (!user || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/orders`)
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { icon: DollarSign, label: 'Total Revenue', value: `₹${stats.total_revenue.toLocaleString()}`, color: 'bg-green-500' },
    { icon: ShoppingCart, label: 'Total Orders', value: stats.total_orders, color: 'bg-blue-500' },
    { icon: Package, label: 'Products', value: stats.total_products, color: 'bg-purple-500' },
    { icon: Users, label: 'Customers', value: stats.total_users, color: 'bg-orange-500' },
    { icon: TrendingUp, label: 'Pending Orders', value: stats.pending_orders, color: 'bg-yellow-500' },
    { icon: Mail, label: 'Subscribers', value: stats.total_subscribers, color: 'bg-pink-500' }
  ] : [];

  const menuItems = [
    { icon: Package, label: 'Products', href: '/admin/products', desc: 'Manage product catalog' },
    { icon: Layers, label: 'Categories', href: '/admin/categories', desc: 'Manage categories' },
    { icon: ShoppingCart, label: 'Orders', href: '/admin/orders', desc: 'View and manage orders' },
    { icon: Users, label: 'Users', href: '/admin/users', desc: 'Manage customers' },
    { icon: FileText, label: 'Blogs', href: '/admin/blogs', desc: 'Manage blog posts' }
  ];

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-stone-900">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">Welcome back, {user?.name}</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl border border-stone-100 p-4">
              <div className={`h-10 w-10 ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
              <p className="text-2xl font-semibold text-stone-900">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <h2 className="font-semibold text-lg text-stone-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-100 hover:border-primary/50 hover:shadow-md transition-all group"
                >
                  <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-900">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg text-stone-900">Recent Orders</h2>
              <Link to="/admin/orders" className="text-primary text-sm font-medium hover:underline">
                View All
              </Link>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {recentOrders.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No orders yet
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {recentOrders.map((order) => (
                    <div key={order.id} className="p-4 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-stone-900">{order.order_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {order.items.length} items • {order.shipping_address.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusClass(order.status)}`}>
                            {order.status}
                          </span>
                          <p className="text-sm font-semibold text-stone-900 mt-1">₹{order.total}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
